import {useMutation} from '@tanstack/react-query'

import {Error, Success} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'
import {MemberListSchemaResponse} from '../domain'
import {useMember} from '../hook'
import {API} from 'src/feature/bite/domain'
import {useAuthUser} from 'react-auth-kit'
import {useNavigate, useParams} from 'react-router-dom'
import {useGetPackageDetail} from './useQuery'
import {getTimeZone} from 'src/feature/bite/helpers/date'

export function useGetAll() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {list} = useMember()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [{type: 2, key: 'CreatedOn'}],
    paging: {
      currentPageNumber: 1,
      pageSize: list.paging.totalCount,
    },
  }

  return useMutation(
    ['membersAll'],
    async () => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Member/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
      const safeParse = MemberListSchemaResponse.safeParse(Data)
      const parse = MemberListSchemaResponse.parse(Data)

      if (parse.responseResult.result === false) {
        Error(parse.responseResult.exceptionDetail || errorMessage)
        return []
      }

      if (!safeParse.success) {
        Error(safeParse.error.message)
        return []
      }

      return parse.response.dataList
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errorMessage)
      },
    }
  )
}

export function useDecreaseCredit({packageId}: {packageId: string}) {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()
  const {refetch} = useGetPackageDetail(packageId)

  return useMutation(
    ['DecreaseCredit'],
    async ({id}: {id: string}) => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Member/DeductMemberPackageCredit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          id,
          credit: 1,
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.global.error)

        return
      }

      await refetch()

      Success(errors.packageItem.credit_decrease_success)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.global.error)
      },
    }
  )
}

export function useIncreaseCredit({packageId}: {packageId: string}) {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()
  const {refetch} = useGetPackageDetail(packageId)

  return useMutation(
    ['IncreaseCredit'],
    async ({id}: {id: string}) => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Member/AddMemberPackageCredit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          id,
          credit: 1,
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.global.error)

        return
      }

      await refetch()

      Success(errors.packageItem.credit_increase_success)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.global.error)
      },
    }
  )
}

export function useModifyCredit({packageId}: {packageId: string}) {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()
  const {refetch} = useGetPackageDetail(packageId)

  return useMutation(
    ['IncreaseCredit'],
    async ({id, credit}: {id: string; credit: number}) => {
      const timeZoneOffset = getTimeZone()

      // Not prepared for this endpoint yet
      const Response = await fetch(`${API}/Member/DeductMemberPackageCredit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          id,
          credit,
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.global.error)

        return
      }

      await refetch()

      Success(errors.packageItem.credit_decrease_success)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.global.error)
      },
    }
  )
}
